import { Dispatch, SetStateAction } from "react";
import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
  RequestResult,
} from "../../lib";

import { doMETHOD_UPDATE_TASK, doREQUEST_ERROR, doREQUEST_COMPLETE } from "../state";
import { getToken } from "./token_fetcher";
import {
  confirmQuestionnaireDraftSaveStage,
  failQuestionnaireDraftSave,
  questionnaireDraftSaveOwnership,
} from "../../lib/questionnaire_draft/coordinator";
import { QuestionnaireDraftSaveStage } from "../../model/questionnaire_draft";
import { questionnaireDraftSaveIsAmbiguous } from "./questionnaire_draft_save";

export const REQUEST_V2_TASK_QUESTIONNAIRE_UPDATE = "REQUEST_V2_TASK_QUESTIONNAIRE_UPDATE";
export const doREQUEST_V2_TASK_QUESTIONNAIRE_UPDATE = createDoRequestAction(
  REQUEST_V2_TASK_QUESTIONNAIRE_UPDATE,
);

export const requestV2TaskQuestionnaireUpdateActionCreators = {
  doREQUEST_V2_TASK_QUESTIONNAIRE_UPDATE,
};

interface Payload {
  payload: {
    taskId: string;
    requestId: string;
    scheduleRepeat: boolean;
    scheduleDescription: string;
    color: string;
    name: string;
    description: string;
    reminderList?: Record<string, string>[];
    // the recovery copy this save finishes; never sent to the server
    draftSave?: { ticketId: string };
  };
  setRequestResult?: Dispatch<SetStateAction<RequestResult | null>>;
}

// this API won't touch questionnaire content, it only affects the task's config
// so it will be used on live editting task only
export function* requestV2TaskQuestionnaireUpdate({
  payload,
  setRequestResult,
}: Payload): SagaIterator {
  debug("saga*requestTaskQuestionnaireUpdate");

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        taskId: v.string().uuid().exist(),
        requestId: v.string().uuid().exist(),
        scheduleRepeat: v.boolean().optional(),
        scheduleDescription: v.string().optional(),
        color: v.string().optional(),
        reminderList: v.array().optional(),
        name: v.string().optional(),
        description: v.string().optional(),
        draftSave: v.object({ ticketId: v.string().exist() }).optional(),
      }),
    });
  } catch (error) {
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_V2_TASK_QUESTIONNAIRE_UPDATE,
          requestId: payload.requestId,
          error,
        },
        setRequestResult,
      ),
    );
  }

  const {
    scheduleRepeat,
    scheduleDescription,
    taskId,
    color,
    reminderList,
    name,
    description,
    draftSave,
  } = payload;

  // A save that finishes a recovery copy belongs to the session it was made in: past a sign-out or a
  // sign-in as someone else -- a replay after a refresh included -- it is neither sent nor published.
  const ticketId = draftSave?.ticketId;
  const ownsSave = questionnaireDraftSaveOwnership(ticketId);
  if (!ownsSave()) return;

  const accessToken = yield call(getToken);
  if (!ownsSave()) return;

  const result = yield request({
    method: "post",
    url: `${API_URL}/v2/web/task-questionnaire-update`,
    accessToken,
    data: {
      scheduleRepeat,
      scheduleDescription,
      taskId,
      color,
      reminderList,
      name,
      description,
    },
  });

  if (!ownsSave()) return;

  if (!result.success || result.error) {
    const error = result.error || new Error("request did not succeed");
    debug(error);
    // a 401 is replayed after the refresh, under the same ticket
    if (ticketId && result.statusCode !== 401) {
      failQuestionnaireDraftSave(ticketId, {
        ambiguous: questionnaireDraftSaveIsAmbiguous(result),
      });
    }
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_V2_TASK_QUESTIONNAIRE_UPDATE,
          fromPayload: payload,
          requestId: payload.requestId,
          url: "/v2/web/task-questionnaire-update",
          ...result,
          error,
        },
        setRequestResult,
      ),
    );
  }

  delete result.payload.enabled;
  yield put(doMETHOD_UPDATE_TASK(result.payload));
  // a live task's questionnaire and rules cannot change here, so its metadata is the whole save
  if (ticketId) {
    confirmQuestionnaireDraftSaveStage(ticketId, QuestionnaireDraftSaveStage.Content);
    confirmQuestionnaireDraftSaveStage(ticketId, QuestionnaireDraftSaveStage.DisplayStrategy);
  }

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_V2_TASK_QUESTIONNAIRE_UPDATE,
        requestId: payload.requestId,
        ...result,
      },
      setRequestResult,
    ),
  );
}

export function* v2TaskQuestionnaireUpdateSaga() {
  yield takeEvery(REQUEST_V2_TASK_QUESTIONNAIRE_UPDATE as any, requestV2TaskQuestionnaireUpdate);
}
